/**
 * O boneco — a PESSOA, separada da planta onde anda.
 *
 * O spritesheet gerado por código, a passada, a velocidade, a interpolação dos
 * remotos, o nome por cima da cabeça. Tudo o resto — chão, mobília, luz, portas
 * — é do escritório e fica no `OfficeSpace`.
 *
 * O que NÃO está aqui: nada que dependa da planta. `seatAt`, `roomAt`, as
 * paredes — isso é geometria, vive em `space.ts` e entra por PARÂMETRO. É por
 * isso que os lugares sentados chegam num adaptador em vez de este módulo os ir
 * buscar: a fronteira é o que mantém o boneco reutilizável sem saber onde pisa.
 */

import {
  type Facing,
  type Gender,
  POKE_TTL_MS,
  type PresenceStatus,
  type Seat,
  type Snowflake,
} from "@greenvoice/shared";
import type PhaserT from "phaser";

import type { VoiceMarks } from "../store/selectors";
import { useStore } from "../store/store";
import {
  ancaSentada,
  type CharSprite,
  ensureCharTextures,
  frameKey,
  seatDropFor,
  sentadoEDeArte,
  sitKey,
  spriteFor,
} from "./officeArt";
import { ensureOfficeChar } from "./officeChar";
import { ensureOfficeCharFemale } from "./officeCharFemale";
import { caminhoDaLua, escalarLua, LUA_AUSENTE, PROPORCAO_DA_LUA } from "./seloDeEstado";

export interface Avatar {
  container: PhaserT.GameObjects.Container;
  /** O boneco em si — troca-se-lhe a textura para animar direcção e passada. */
  sprite: PhaserT.GameObjects.Image;
  /** Sombra no chão. Esconde-se quando o corpo está sentado num móvel. */
  shadow: PhaserT.GameObjects.Ellipse;
  /** Prefixo da textura deste visual (`spriteFor().key`). */
  colorKey: string;
  /** Para onde está virado — decide qual das 4 filas de frames se mostra. */
  dir: Facing;
  /** Acumulador de tempo do walk-cycle, em ms. */
  walkT: number;
  /** Índice no ciclo de passada (0..3). */
  frame: number;
  /** Em que lugar está sentado, se estiver. `null` = de pé. */
  seat: Seat | null;
  /** O nome por cima da cabeça. Exposto porque os selos se alinham com ele. */
  label: PhaserT.GameObjects.Text;
  /** Os selos ao lado do nome — micro cortado, som cortado, ausente. */
  marks: PhaserT.GameObjects.Image[];
  /** Assinatura dos selos desenhados. Rever isto a 60fps é o que evita refazer
   *  três imagens por avatar em cada frame para dizer exactamente o mesmo. */
  markKey: string;
}

export interface Remote extends Avatar {
  /** Última posição conhecida — o alvo para onde se interpola. */
  targetX: number;
  targetY: number;
  /**
   * Se ainda está a caminho do alvo. Guardado porque quem reparte os lugares
   * precisa de saber quem está PARADO, e essa conta já é feita aqui uma vez por
   * frame — recalculá-la fora era arriscar duas respostas para a mesma pergunta.
   */
  moving: boolean;
}

/**
 * Os lugares onde uma pessoa se pode sentar, num mundo que os tenha.
 *
 * Três perguntas, não cinco: a POSE de quem se senta (a textura e a descida até
 * à almofada) sai do tipo de assento, não do mapa, e por isso continua a ser
 * resolvida pelo `officeArt` sem passar por aqui. O que muda de mundo para mundo
 * é só ONDE há assentos, ONDE encaixa o corpo e a que profundidade fica.
 */
export interface Seating {
  /** O assento pisado por um corpo parado nesta posição, se houver. */
  at(x: number, y: number): Seat | null;
  /** O ponto exacto onde o corpo encaixa ao sentar-se. */
  anchor(seat: Seat): { x: number; y: number };
  /** A profundidade de quem lá está — à frente do móvel, atrás do encosto. */
  depth(seat: Seat): number;
  /**
   * De quem é este lugar neste instante — `undefined` se estiver livre.
   *
   * Um lugar é de uma pessoa só, e quem decide não é quem chegou primeiro (isso
   * seria diferente em cada máquina) mas uma conta igual em toda a parte, feita
   * sobre as posições que já circulam. Sem esta pergunta, dois corpos parados no
   * mesmo tile sentavam-se ambos e ficavam um por cima do outro.
   */
  ownerOf(seat: Seat): string | undefined;
}

/** Velocidade do jogador local, em pixels por segundo (~4.7 tiles/s). */
export const SPEED = 150;
/** Fracção do caminho até ao alvo por frame — o "peso" da interpolação remota. */
export const LERP = 0.2;
/** A partir de que distância (8 tiles) uma posição nova é um salto e não um passo. */
export const SALTO_REMOTO_PX = 8 * 32;
/** Meia-largura de colisão: pára o corpo do avatar na parede, não o centro. */
export const COLLIDE_MARGIN = 10;
/** Zoom da câmara — aproxima o mundo para dar a escala aconchegada de um jogo. */
export const CAMERA_ZOOM = 1.75;
/**
 * Até onde se pode AFASTAR a câmara.
 *
 * O jardim tem 44×52 tiles = 1408×1664px, e quem manda no número é a ALTURA:
 * num palco de 800px é preciso descer a 0.48 para a faixa inteira caber (a
 * largura já cabia a 0.91). 0.45 dá isso com uma margem — é o "ver o escritório
 * todo de uma vez" medido, não escolhido a olho. Abaixo disto o avatar fica um
 * ponto e as etiquetas das salas deixam de se ler, o que já não serve para nada.
 */
export const ZOOM_MIN = 0.45;
/** E até onde se pode APROXIMAR: mais do que isto e vê-se meia secretária. */
export const ZOOM_MAX = 2.6;
/**
 * Um degrau de zoom. É MULTIPLICATIVO, não aditivo: em 0.5 um passo de +0.25
 * dá meio mundo a mais e em 2.5 quase não se nota. Multiplicar dá o mesmo salto
 * VISUAL em qualquer ponto da escala, que é o que a mão espera da roda do rato.
 */
export const ZOOM_STEP = 1.18;

/**
 * Os limites a dar à câmara para uma faixa — já com o campo de visão em conta.
 *
 * Existe por causa de uma coisa que o Phaser NÃO faz e que toda a gente assume
 * que faz. Quando se afasta até a vista ficar mais LARGA do que a faixa, o
 * `clampX` da câmara não centra o mundo: encosta-o à esquerda. A conta dele é
 *
 *   bx = limites.x + (vista - câmara) / 2
 *   bw = max(bx, bx + limites.largura - vista)
 *
 * e com `vista > limites.largura` o segundo termo fica abaixo do primeiro, os
 * dois colapsam em `bx`, e o canto esquerdo da vista passa a ser exactamente
 * `limites.x` — seja qual for o zoom. Com os limites colados ao mapa
 * (`limites.x = 0`) isso é o mapa agarrado à esquerda e o resto do ecrã preto,
 * que foi a queixa: "em vez de o mapa estar centralizado, ele fica pro canto
 * esquerdo".
 *
 * Como `limites.x` É o canto esquerdo da vista nesse regime, centrar é escolher
 * o `limites.x` certo: alarga-se a caixa SIMETRICAMENTE até ao tamanho da vista
 * e o mundo fica no meio dela. Enquanto a vista couber na faixa, o `max` devolve
 * a própria faixa e nada disto se nota — é o comportamento de sempre.
 *
 * Fica aqui, e não dentro do `create` do Phaser, porque é aritmética pura: é a
 * única metade desta correcção que se pode provar sem abrir um navegador.
 */
export function bandaCamara(
  faixa: { x: number; y: number; w: number; h: number },
  vista: { w: number; h: number },
): { x: number; y: number; w: number; h: number } {
  const w = Math.max(faixa.w, vista.w);
  const h = Math.max(faixa.h, vista.h);
  return { x: faixa.x + (faixa.w - w) / 2, y: faixa.y + (faixa.h - h) / 2, w, h };
}

/** Duração de cada passo do walk-cycle, em ms. */
export const STEP_MS = 140;
/** O ciclo de passada: esquerda → parado → direita → parado. */
export const WALK_CYCLE = [0, 1, 2, 1] as const;

/**
 * Avança a animação de um avatar: cicla a passada quando anda, mostra o frame
 * neutro quando pára. Muda só a textura — o Phaser já a tem em cache.
 */
export function stepAnim(av: Avatar, moving: boolean, delta: number): void {
  if (av.seat) return; // sentado não tem passada: a pose é fixa
  if (moving) {
    av.walkT += delta;
    if (av.walkT >= STEP_MS) {
      av.walkT -= STEP_MS;
      av.frame = (av.frame + 1) % WALK_CYCLE.length;
    }
  } else {
    av.frame = 0;
    av.walkT = 0;
  }
  const f = moving ? (WALK_CYCLE[av.frame] ?? 1) : 1;
  av.sprite.setTexture(frameKey(av.colorKey, av.dir, f));
}

/**
 * Senta ou levanta o boneco. É a mesma ideia das portas: ninguém MANDA sentar-se
 * — pisou um assento e parou, logo está sentado. A regra corre igual em todos os
 * clientes a partir de posições que já circulam, por isso não há mensagem nova
 * no protocolo e toda a gente vê a mesma pessoa no mesmo sofá.
 *
 * Devolve `true` na transição PARA sentado, que é quando o corpo tem de encaixar
 * no centro do lugar (quem chama trata do encaixe: aqui só se manda no desenho).
 */
export function setSeated(av: Avatar, seat: Seat | null): boolean {
  if (av.seat && seat && av.seat.col === seat.col && av.seat.row === seat.row) return false;
  if (!av.seat && !seat) return false;
  av.seat = seat;
  if (!seat) {
    av.sprite.setY(0);
    av.shadow.setVisible(true);
    return false;
  }
  // A direcção vem do LUGAR, não de onde a pessoa vinha a andar: quem se senta a
  // uma mesa fica virado para ela — chegar pela esquerda não te põe de costas.
  av.dir = seat.facing;
  const tex = sitKey(av.colorKey, seat.facing);
  av.sprite.setTexture(tex);
  // O rabo assenta na almofada, não no ar. A linha das ancas depende de QUEM
  // encheu esta chave (arte ou boneco desenhado) e do MÓVEL: a anca da arte só
  // conta nos sofás — nas cadeiras a altura já foi afinada com ela, e aplicá-la
  // outra vez afundava o boneco abaixo do assento (ver `ancaSentada`).
  av.sprite.setY(seatDropFor(seat.kind, ancaSentada(seat.kind, sentadoEDeArte(tex))));
  av.shadow.setVisible(false); // o corpo está no móvel: sombra no chão seria mentira
  av.walkT = 0;
  av.frame = 0;
  return true;
}

/**
 * Este boneco ainda é o da personagem escolhida?
 *
 * A chave da textura leva o género no prefixo (ver `spriteFor`), portanto
 * comparar chaves responde à pergunta sem campo novo em lado nenhum e sem uma op
 * nova no protocolo: a mudança viaja no `UserUpdate` que já circula.
 *
 * Faz falta porque o boneco escolhe a arte UMA vez, ao nascer. Sem isto, trocar
 * de personagem não se via até recarregar a página — e quem trocou ficava a
 * olhar para o boneco antigo a pensar que não tinha resultado.
 */
export function mudouDePersonagem(av: Avatar, id: string, gender: Gender): boolean {
  return av.colorKey !== spriteFor(id, gender).key;
}

/** Um avatar = sombra no chão + boneco pixel-art (origem nos pés) + nome por cima. */
export function makeAvatar(
  scene: PhaserT.Scene,
  x: number,
  y: number,
  id: string,
  name: string,
  gender: Gender,
): Avatar {
  const sprite: CharSprite = spriteFor(id, gender);
  // A arte do pacote reclama as chaves primeiro; o `ensureCharTextures` encontra-as
  // ocupadas e desiste sozinho, frame a frame. Invertida, a ordem não fazia nada —
  // e o escritório voltava aos bonecos desenhados sem um erro. Cada género tem a
  // SUA folha: o masculino no `personagem-v1` (andar + sentado), o feminino em
  // quatro PNG de andar (`officeCharFemale`). A menina fica de pé com a arte real
  // e SENTADA com a pose desenhada em código — o `ensureOfficeCharFemale` não
  // reclama o `sitKey`, por isso é o `ensureCharTextures` (que respeita
  // `sprite.female`) a desenhar-lhe o sentado de cabelo comprido. A chave leva o
  // género no prefixo, por isso não há colisão de cache entre os dois.
  if (gender === "FEMALE") ensureOfficeCharFemale(scene, sprite.key);
  else ensureOfficeChar(scene, sprite.key);
  ensureCharTextures(scene, sprite);
  const shadow = scene.add.ellipse(0, -1, 26, 9, 0x000000, 0.3);
  const image = scene.add.image(0, 0, frameKey(sprite.key, "down", 1)).setOrigin(0.5, 1);
  const label = scene.add
    .text(0, -54, name, {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: "11px",
      color: "#eef4fb",
      stroke: "#0b0f14",
      strokeThickness: 3,
    })
    .setOrigin(0.5, 0.5)
    .setResolution(2);
  const container = scene.add.container(x, y, [shadow, image, label]);
  return {
    container,
    sprite: image,
    shadow,
    colorKey: sprite.key,
    dir: "down",
    walkT: 0,
    frame: 0,
    seat: null,
    label,
    marks: [],
    markKey: "",
  };
}

// ── Os selos ao lado do nome ──────────────────────────────────────────────────

/** O que se pode dizer de alguém sem ele falar: está calado, não ouve, não está —
 *  ou está mesmo ali, à espera que lhe falem. */
export type AvatarMark = "mic" | "deaf" | "away" | "online";

/** Lado do selo no mundo, em pixels. O nome tem 11px — um selo maior roubava-lhe
 *  a leitura, e mais pequeno já não se distinguia um microfone de uns fones. */
const MARK_SIZE = 11;
/** Folga entre o nome e o primeiro selo, e entre selos. */
const MARK_GAP = 3;
/** O canvas é desenhado ao dobro e mostrado a metade: num ecrã retina os riscos
 *  finos do microfone ficam nítidos em vez de uma mancha cinzenta. */
const MARK_PX = MARK_SIZE * 2;

const MARK_KEY: Record<AvatarMark, string> = {
  mic: "mark:mic",
  deaf: "mark:deaf",
  away: "mark:away",
  online: "mark:online",
};

/** Raio do disco de cor dos glifos, na grelha de 22 px em que estão desenhados. */
export const RAIO_DO_GLIFO = 6.5;

/**
 * A lua de «ausente» na grelha de 22 px — a MESMA de `seloDeEstado.ts`, só
 * escalada. Não é uma segunda tabela de números: é a canónica posta aqui, e o
 * teste verifica que continua a ser.
 *
 * Vive fora do `ctx.arc` porque os dois números que decidem se aquilo se lê como
 * lua ou como selo estragado — a espessura que sobra do crescente e o aro de cor
 * que fica por fora dele — não estavam escritos em lado nenhum. Enterrada numa
 * chamada, uma dentada errada não parte nada, não dá erro e fica à espera de uma
 * fotografia a dizer que está feio. Foi assim que a sombra das casas se partiu
 * duas vezes na mesma semana.
 */
export const LUA_DO_MAPA = escalarLua(LUA_AUSENTE, 11, 11, RAIO_DO_GLIFO * PROPORCAO_DA_LUA);

/**
 * Que selos mostrar. Função pura de propósito: é a única regra do conjunto que
 * se pode provar sem abrir um navegador, e é a que interessa acertar.
 *
 * O silêncio imposto por um moderador desenha-se igual ao que a pessoa escolheu:
 * quem olha quer saber se ADIANTA falar com ela, e para essa pergunta a origem
 * do silêncio é irrelevante. Ficar surdo implica micro cortado no cliente, mas os
 * dois selos aparecem à mesma — "não te ouve" e "não fala" são coisas diferentes
 * para quem está a decidir se se aproxima.
 *
 * Ausente e disponível ocupam O MESMO lugar e excluem-se: são a resposta à mesma
 * pergunta ("vale a pena chamar por esta pessoa?"), e duas respostas ao mesmo
 * tempo não é mais informação, é ruído. Antes só se marcava a ausência, o que
 * deixava "disponível" a ler-se por AUSÊNCIA de selo — e ausência de selo é
 * também o que se vê em quem está ocupado, invisível ou por carregar. Um selo
 * verde afirma; nada não afirma coisa nenhuma.
 *
 * Ocupado e invisível continuam sem selo de propósito: "não incomodar" já se diz
 * na lista, e o invisível deixaria de o ser no instante em que o mundo o pintasse.
 */
export function marksFor(voice: VoiceMarks | undefined, status: PresenceStatus | undefined): AvatarMark[] {
  const marks: AvatarMark[] = [];
  if (voice && (voice.selfMute || voice.serverMute)) marks.push("mic");
  if (voice && (voice.selfDeaf || voice.serverDeaf)) marks.push("deaf");
  if (status === "IDLE") marks.push("away");
  else if (status === "ONLINE") marks.push("online");
  return marks;
}

/** Desenha os quatro glifos uma vez por cena. Canvas e não SVG: o Phaser precisa
 *  de uma textura, e quatro canvas de 22px custam menos que um pedido de rede. */
export function ensureMarkTextures(scene: PhaserT.Scene): void {
  for (const kind of ["mic", "deaf", "away", "online"] as const) {
    const key = MARK_KEY[kind];
    if (scene.textures.exists(key)) continue;
    const canvas = document.createElement("canvas");
    canvas.width = MARK_PX;
    canvas.height = MARK_PX;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Disco de fundo escuro: o selo tem de se ler por cima de relva, de betão e
    // de uma parede clara. Sem ele, o traço vermelho desaparecia em metade do mapa.
    const fundo = (): void => {
      ctx.fillStyle = "rgba(11,15,20,0.82)";
      ctx.beginPath();
      ctx.arc(MARK_PX / 2, MARK_PX / 2, MARK_PX / 2 - 0.5, 0, Math.PI * 2);
      ctx.fill();
    };
    fundo();

    // Verde igual ao ponto da lista (`--green`, #22c55e): o mesmo estado não pode
    // ter duas cores conforme se olhe para a barra lateral ou para o mapa.
    ctx.strokeStyle = kind === "away" ? "#f0b23c" : kind === "online" ? "#22c55e" : "#f2555a";
    ctx.fillStyle = ctx.strokeStyle;
    ctx.lineWidth = 1.8;
    ctx.lineCap = "round";
    const s = MARK_PX / 22; // as coordenadas abaixo estão numa grelha de 22px

    if (kind === "mic") {
      // Cápsula + haste + base: um microfone de estúdio visto de frente.
      ctx.beginPath();
      ctx.roundRect(9 * s, 5 * s, 4 * s, 8 * s, 2 * s);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(11 * s, 15.5 * s);
      ctx.lineTo(11 * s, 17.5 * s);
      ctx.moveTo(8 * s, 17.5 * s);
      ctx.lineTo(14 * s, 17.5 * s);
      ctx.stroke();
    } else if (kind === "deaf") {
      // Arco por cima + duas almofadas: uns auscultadores.
      ctx.beginPath();
      ctx.arc(11 * s, 12 * s, 6 * s, Math.PI, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.roundRect(3.6 * s, 11.5 * s, 3.2 * s, 5.5 * s, 1.4 * s);
      ctx.roundRect(15.2 * s, 11.5 * s, 3.2 * s, 5.5 * s, 1.4 * s);
      ctx.fill();
    } else if (kind === "away") {
      // Disco CHEIO com o crescente cavado por DENTRO — a mesma gramática do
      // selo do avatar (`seloDeEstado.ts`), guardada pelo teste: o mesmo estado
      // não pode ser uma lua na barra lateral e outra coisa no mapa. Um "z" a
      // 11px seria um borrão, e um crescente com o contorno aberto lê-se como um
      // selo partido — foi essa a queixa, quatro vezes.
      const l = LUA_DO_MAPA;
      ctx.beginPath();
      ctx.arc(11 * s, 11 * s, RAIO_DO_GLIFO * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = "destination-out";
      ctx.fill(new Path2D(caminhoDaLua(escalarLua(l, l.ox * s, l.oy * s, l.raio * s))));
      // O `destination-out` apaga ALFA, e o alfa que estava ali por baixo era o
      // disco escuro — a lua ficava um buraco a deixar passar o chão, verde na
      // relva e creme na parede. O `destination-over` repõe o fundo só onde o
      // alfa ficou a zero, portanto o recorte volta a ser escuro sem tocar na cor.
      ctx.globalCompositeOperation = "destination-over";
      fundo();
      ctx.globalCompositeOperation = "source-over";
    } else {
      // Disco CHEIO — e é a forma, não a cor, que o distingue da ausência: a lua
      // tem uma dentada, este não tem nenhuma. A 11px a cor ainda se lê, mas quem
      // não distingue verde de âmbar continua a ver "inteiro" contra "mordido".
      // É também o mesmo desenho do ponto na barra lateral, de propósito.
      ctx.beginPath();
      ctx.arc(11 * s, 11 * s, 6.5 * s, 0, Math.PI * 2);
      ctx.fill();
      // Lustro em cima, do lado da luz: sem ele o disco lê-se como um autocolante
      // chapado, com ele lê-se como uma luz acesa — que é o que se quer dizer.
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = "#eafff1";
      ctx.beginPath();
      ctx.arc(9 * s, 9 * s, 2.1 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // A barra cortada é o que transforma "microfone" em "microfone DESLIGADO" —
    // sem ela o selo dizia o contrário do que se quer dizer.
    if (kind === "mic" || kind === "deaf") {
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.moveTo(4.5 * s, 4.5 * s);
      ctx.lineTo(17.5 * s, 17.5 * s);
      ctx.strokeStyle = "#f2555a";
      ctx.stroke();
    }
    scene.textures.addCanvas(key, canvas);
  }
}

/**
 * Põe os selos À FRENTE do nome, e volta a centrar o conjunto.
 *
 * À frente e não atrás porque um nome comprido empurrava o selo para longe da
 * cabeça — e o selo é a parte que se lê primeiro: "esta pessoa ouve-me?" vem
 * antes de "quem é esta pessoa", que o boneco já responde. Com os selos à
 * esquerda, o sinal cai sempre no mesmo sítio em relação ao avatar, seja o nome
 * "Zé" ou "Kawã Oliveira".
 *
 * O nome está centrado no avatar. Pendurar selos de um lado sem mexer no resto
 * empurrava visualmente a pessoa para o outro — o nome deixava de estar por cima
 * da cabeça dela. Por isso mede-se o grupo INTEIRO (selos + nome) e desloca-se o
 * nome meia-largura de selos para a direita: o centro do conjunto volta a
 * coincidir com o centro do boneco.
 */
export function setMarks(scene: PhaserT.Scene, av: Avatar, kinds: AvatarMark[]): void {
  const key = kinds.join(",");
  if (key === av.markKey) return;
  av.markKey = key;

  for (const mark of av.marks) mark.destroy();
  av.marks = [];

  if (kinds.length === 0) {
    av.label.setX(0);
    return;
  }
  ensureMarkTextures(scene);

  const extra = kinds.length * MARK_SIZE + kinds.length * MARK_GAP;
  const labelWidth = av.label.width;
  const left = -(labelWidth + extra) / 2;
  av.label.setX(left + extra + labelWidth / 2);

  kinds.forEach((kind, index) => {
    const image = scene.add
      .image(left + index * (MARK_SIZE + MARK_GAP) + MARK_SIZE / 2, av.label.y, MARK_KEY[kind])
      .setDisplaySize(MARK_SIZE, MARK_SIZE);
    av.container.add(image);
    av.marks.push(image);
  });
}

/**
 * O tremor de quem levou um toque, em pixels de deslocamento horizontal.
 *
 * Uma sinusoide que MORRE (a amplitude desce com o tempo) em vez de um número
 * aleatório por frame: um estremeção que se apaga lê-se como "levou um toque
 * agora", e um ruído constante lê-se como um bug de desenho. Fora da janela
 * devolve zero exacto — é isso que garante que o boneco assenta no sítio certo e
 * não fica meio pixel torto para sempre.
 */
export function pokeShake(startedAt: number | undefined, now: number): number {
  if (startedAt === undefined) return 0;
  const age = now - startedAt;
  if (age < 0 || age >= POKE_TTL_MS) return 0;
  return Math.sin(age / 26) * 5 * (1 - age / POKE_TTL_MS);
}

/**
 * Alinha os avatares remotos com o snapshot do store: cria os que apareceram,
 * interpola os presentes até à última posição conhecida, anima-os e destrói os
 * que saíram. Corre a cada frame — daí ler o store imperativamente, sem hook.
 *
 * O `spaceId` é a chave do snapshot: quem está noutro espaço vive noutra entrada
 * do `positions` e simplesmente não aparece aqui. Hoje há um espaço só por
 * servidor (o escritório), mas a chave continua a ser um parâmetro — é o que
 * impedia gente de mundos diferentes de se ver, e custa nada mantê-lo.
 */
export function reconcileRemotes(
  scene: PhaserT.Scene,
  spaceId: Snowflake,
  remotes: Map<string, Remote>,
  delta: number,
  seating: Seating | null = null,
): void {
  const store = useStore.getState();
  const snapshot = store.positions[spaceId] ?? {};
  const pokes = store.spacePokes[spaceId] ?? {};
  const now = Date.now();

  for (const [userId, occ] of Object.entries(snapshot)) {
    let remote = remotes.get(userId);
    // Trocou de personagem enquanto estava à vista? Deita-se fora e nasce outro
    // no mesmo sítio — é o caminho que já existe para quem entra, reutilizado.
    // Mexer nas texturas do boneco vivo seria mais código para o mesmo fim.
    if (remote && mudouDePersonagem(remote, userId, store.users[userId]?.gender ?? "MALE")) {
      remote.container.destroy();
      remotes.delete(userId);
      remote = undefined;
    }
    if (!remote) {
      const user = store.users[userId];
      const name = user ? (user.displayName ?? user.username) : "Alguém";
      const made = makeAvatar(scene, occ.x, occ.y, userId, name, user?.gender ?? "MALE");
      remote = { ...made, targetX: occ.x, targetY: occ.y, moving: false };
      remotes.set(userId, remote);
    } else {
      remote.targetX = occ.x;
      remote.targetY = occ.y;
      remote.dir = occ.facing;
    }
    const dx = remote.targetX - remote.container.x;
    const dy = remote.targetY - remote.container.y;
    // Um salto maior do que qualquer passo (a escada troca de andar somando 66
    // filas, a escotilha atravessa o mapa): pousa-se já no destino. Interpolado,
    // o boneco atravessava o mapa inteiro a deslizar durante meio segundo.
    if (Math.hypot(dx, dy) > SALTO_REMOTO_PX) {
      remote.container.x = remote.targetX;
      remote.container.y = remote.targetY;
    }
    const moving = Math.hypot(remote.targetX - remote.container.x, remote.targetY - remote.container.y) > 0.6;
    remote.moving = moving;
    remote.container.x += (remote.targetX - remote.container.x) * LERP;
    remote.container.y += (remote.targetY - remote.container.y) * LERP;
    // Parado em cima de um assento ⇒ sentado. A MESMA regra que o cliente aplica
    // a si próprio, sobre a posição que este já recebeu — sem op nova no protocolo.
    //
    // E só se senta o DONO do lugar. O movimento já impede quase sempre que se
    // chegue a um assento tomado, mas "quase sempre" não chega: um passo pode
    // cruzar-se com a chegada da posição do outro pela rede, e nesse instante
    // ficavam dois bonecos encaixados na mesma almofada. Aqui a resposta é uma
    // só, igual em todos os ecrãs — quem não é dono continua de pé.
    const pisado = seating && !moving ? seating.at(remote.container.x, remote.container.y) : null;
    const seat = pisado && seating && seating.ownerOf(pisado) !== userId ? null : pisado;
    if (setSeated(remote, seat) && seat && seating) {
      const anchor = seating.anchor(seat);
      remote.container.x = anchor.x;
      remote.container.y = anchor.y;
    }
    remote.container.setDepth(remote.seat && seating ? seating.depth(remote.seat) : remote.container.y);
    stepAnim(remote, moving, delta);
    setMarks(scene, remote, marksFor(store.voiceStates[userId], store.users[userId]?.status));
    // O tremor vai no BONECO, não no contentor: o contentor é interpolado até à
    // última posição recebida a cada frame, e mexer-lhe aqui era empurrar a
    // pessoa para o lado de verdade — o nome e os selos iam atrás, e o avatar
    // acabava o toque uns pixels ao lado de onde o servidor o julga.
    remote.sprite.setX(pokeShake(pokes[userId]?.at, now));
  }

  for (const [userId, remote] of remotes) {
    if (!snapshot[userId]) {
      remote.container.destroy();
      remotes.delete(userId);
    }
  }
}

/** Cria uma textura de brilho radial (centro opaco → borda transparente) uma vez. */
export function ensureGlow(scene: PhaserT.Scene, key: string, rgb: string): void {
  if (scene.textures.exists(key)) return;
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, `rgba(${rgb},1)`);
  g.addColorStop(0.55, `rgba(${rgb},0.35)`);
  g.addColorStop(1, `rgba(${rgb},0)`);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  scene.textures.addCanvas(key, canvas);
}

export const GLOW_WARM = "glow:warm";
export const GLOW_COOL = "glow:cool";
export const GLOW_GREEN = "glow:green"; // fósforo dos CRT dos postos de trabalho
export const GLOW_RED = "glow:red"; // luz de sala trancada
